import React, { useState, useEffect, useCallback, useContext, useMemo } from "react";
import {
  getAgreement,
  getCurrentAgreementApproval,
  getAgreementApprovals,
  deleteAgreementApproval,
} from "../../../../../api/agreementsApi";
import {
  APPROVAL_STATUS_LABELS,
  APPROVAL_STEP_STATUS_LABELS,
  AGREEMENT_STATUSES_WITH_APPROVAL_PROCESS,
  canShowDeleteApprovalAction,
  getDeleteApprovalTitle,
  isAgreementApprovalInitiator,
  pickLatestApprovalFromHistory,
  resolveAgreementApprovalId,
  resolveAgreementListStatus,
  resolveApprovalInitiatorName,
} from "../agreementsListHelpers";
import { UserContext } from "../../../../../contexts/CurrentUserContext";
import {
  buildPaymentRows,
  formatDateRu,
  normalizeWorkflowStages,
  getCurrentWorkflowStageFromList,
  resolvePaymentStatus,
  PAYMENT_STATUS_LABELS as SHARED_PAYMENT_STATUS_LABELS,
} from "../../AgreementsV2/agreementsHelpers";
import {
  buildSectionDetailsFromDetailed,
  getProjectEstimate,
} from "../../../../../api/projectEstimatesApi";
import { getAgreementSectionModuleTitles } from "../agreementFormHelpers";
import "./AgreementDetail.css";

const toNumericId = (value) => {
  const id = Number(value);
  return Number.isFinite(id) ? id : null;
};

const readExpenditureBlockId = (expenditure) =>
  toNumericId(
    expenditure?.id ??
      expenditure?.block_id ??
      expenditure?.block?.id ??
      expenditure?.block?.tei_id,
  );

const readExpenditureBlockName = (expenditure) =>
  expenditure?.block?.name || expenditure?.name || expenditure?.block_name || null;

const buildAgreementSectionCatalog = (estimate) => {
  const detailedBySectionId = buildSectionDetailsFromDetailed(
    estimate?.construction_design_detailed,
  );
  const catalog = {};

  const addSection = (item) => {
    const sectionId = toNumericId(item?.section?.id);
    if (sectionId == null) return;
    const expenditures =
      detailedBySectionId[sectionId]?.block_expenditures ||
      item?.block_expenditures ||
      [];
    const blockNameById = {};
    const blockIds = [];
    expenditures.forEach((expenditure) => {
      const blockId = readExpenditureBlockId(expenditure);
      if (blockId == null) return;
      blockIds.push(blockId);
      const name = readExpenditureBlockName(expenditure);
      if (name && blockNameById[blockId] == null) {
        blockNameById[blockId] = name;
      }
    });
    catalog[sectionId] = {
      name: item?.section?.name || `Раздел #${sectionId}`,
      blockIds,
      blockNameById,
    };
  };

  (estimate?.contract_sections?.sections || []).forEach(addSection);
  (estimate?.construction_design || []).forEach(addSection);
  (estimate?.construction_design_project || []).forEach(addSection);
  return catalog;
};

const getAgreementBlockEntries = (agreement) => {
  const details = Array.isArray(agreement?.agreement_blocks_detail)
    ? agreement.agreement_blocks_detail
    : [];
  if (details.length > 0) {
    return details
      .map((item) => ({
        id: toNumericId(item?.block_id),
        name: item?.block_name || null,
      }))
      .filter((item) => item.id != null);
  }

  const blocks = Array.isArray(agreement?.agreement_blocks)
    ? agreement.agreement_blocks
    : [];
  if (blocks.length > 0) {
    return blocks
      .map((item) => ({
        id: toNumericId(item?.block_id),
        name: item?.block_name || null,
      }))
      .filter((item) => item.id != null);
  }

  return (Array.isArray(agreement?.block_ids) ? agreement.block_ids : [])
    .map((id) => ({ id: toNumericId(id), name: null }))
    .filter((item) => item.id != null);
};

const resolveBlockName = (block, section) =>
  block?.name ||
  section?.blockNameById?.[block?.id] ||
  (block?.id != null ? `Блок #${block.id}` : null);

const formatSectionBlockLabel = (sectionName, blockName) => {
  if (sectionName && blockName) return `${sectionName} · ${blockName}`;
  return sectionName || blockName || "—";
};

const buildAgreementSectionBlockLabels = (agreement, catalog) => {
  if (!agreement) return [];

  const sectionIds = (
    Array.isArray(agreement.section_ids) ? agreement.section_ids : []
  )
    .map((id) => toNumericId(id))
    .filter((id) => id != null);
  const pricedSectionIds = new Set(
    (Array.isArray(agreement.section_prices) ? agreement.section_prices : [])
      .map((item) => toNumericId(item?.section_id))
      .filter((id) => id != null),
  );
  const labelSection = (sectionId, sectionName) => {
    const titles = getAgreementSectionModuleTitles(agreement, sectionId);
    if (!pricedSectionIds.has(sectionId) || titles.length === 0) {
      return sectionName;
    }
    return `${sectionName} · ${titles.join(", ")}`;
  };
  const blocks = getAgreementBlockEntries(agreement);
  const catalogReady = catalog != null && Object.keys(catalog).length > 0;
  const catalogHasBlocks =
    catalogReady &&
    sectionIds.some(
      (sectionId) => (catalog[sectionId]?.blockIds || []).length > 0,
    );

  if (sectionIds.length === 0) {
    return blocks.map((block) => resolveBlockName(block, null)).filter(Boolean);
  }

  if (catalogHasBlocks) {
    const remaining = [...blocks];
    const labels = [];

    sectionIds.forEach((sectionId) => {
      const section = catalog[sectionId];
      const sectionName = section?.name || `Раздел #${sectionId}`;
      const sectionBlockIds = section?.blockIds || [];

      if (sectionBlockIds.length === 0) {
        labels.push(labelSection(sectionId, sectionName));
        return;
      }

      const available = new Map();
      sectionBlockIds.forEach((blockId) => {
        available.set(blockId, (available.get(blockId) || 0) + 1);
      });

      const taken = [];
      while (remaining.length > 0) {
        const next = remaining[0];
        const count = available.get(next.id) || 0;
        if (count <= 0) break;
        available.set(next.id, count - 1);
        remaining.shift();
        taken.push(next);
      }

      if (taken.length === 0) {
        labels.push(labelSection(sectionId, sectionName));
        return;
      }

      taken.forEach((block) => {
        labels.push(
          formatSectionBlockLabel(sectionName, resolveBlockName(block, section)),
        );
      });
    });

    remaining.forEach((block) => {
      const name = resolveBlockName(block, null);
      if (name) labels.push(name);
    });

    return labels;
  }

  if (sectionIds.length === blocks.length && blocks.length > 0) {
    return sectionIds.map((sectionId, index) => {
      const section = catalog?.[sectionId];
      return formatSectionBlockLabel(
        section?.name || `Раздел #${sectionId}`,
        resolveBlockName(blocks[index], section),
      );
    });
  }

  if (blocks.length > 0) {
    return blocks.map((block) => resolveBlockName(block, null)).filter(Boolean);
  }

  return sectionIds.map((sectionId) =>
    labelSection(
      sectionId,
      catalog?.[sectionId]?.name || `Раздел #${sectionId}`,
    ),
  );
};

const STAGE_STATUS_LABELS = {
  completed: "Завершён",
  in_progress: "В процессе",
  not_started: "Не начат",
};

const APPROVER_TIMELINE_STATUS_LABELS = {
  approved: "Согласован",
  review: "На рассмотрении",
  rejected: "Отклонён",
};

const toTimelineStatus = (status) => {
  if (
    status === "done" ||
    status === "completed" ||
    status === "approved" ||
    status === "signed"
  ) {
    return "completed";
  }
  return status || "not_started";
};

const toApproverTimelineStatus = (status) => {
  if (
    status === "done" ||
    status === "completed" ||
    status === "approved" ||
    status === "signed"
  ) {
    return "approved";
  }
  if (status === "rejected") return "rejected";
  return "review";
};

const PAYMENT_STATUS_LABELS = {
  ...SHARED_PAYMENT_STATUS_LABELS,
  expected_ODGE: "Ожидается",
  expected_OPGE: "Ожидается",
};

const getApprovalStatusClass = (status) => {
  if (status === "approved" || status === "signed")
    return "agreement-detail-approval-status--approved";
  if (status === "rejected") return "agreement-detail-approval-status--rejected";
  if (status === "pending") return "agreement-detail-approval-status--pending";
  return "";
};

const getStepStatusClass = (status) => {
  if (status === "approved" || status === "signed")
    return "agreement-detail-step--approved";
  if (status === "rejected") return "agreement-detail-step--rejected";
  if (status === "pending") return "agreement-detail-step--pending";
  return "";
};

const ApprovalStepsTable = ({ steps }) => {
  const sortedSteps = [...(steps || [])].sort(
    (a, b) => (a.step_order ?? 0) - (b.step_order ?? 0),
  );

  if (sortedSteps.length === 0) {
    return <p className="agreement-detail-approval-empty">Шаги не назначены</p>;
  }

  return (
    <div className="agreement-detail-table-wrap">
      <table className="agreement-detail-table agreement-detail-approval-table">
        <thead>
          <tr>
            <th>№</th>
            <th>Согласующий</th>
            <th>Статус</th>
            <th>Комментарий</th>
            <th>Дата решения</th>
          </tr>
        </thead>
        <tbody>
          {sortedSteps.map((step) => (
            <tr key={step.id ?? step.step_order}>
              <td>{step.step_order ?? "—"}</td>
              <td>{step.approver_name ?? `ID ${step.approver_id ?? "—"}`}</td>
              <td>
                <span
                  className={`agreement-detail-approval-status ${getStepStatusClass(step.status)}`}
                >
                  {APPROVAL_STEP_STATUS_LABELS[step.status] ??
                    step.status ??
                    "—"}
                </span>
              </td>
              <td>{step.comment?.trim() ? step.comment : "—"}</td>
              <td>{formatDateTime(step.decided_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return "—";
  try {
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return dateStr;
    return d.toLocaleString("ru-RU", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return dateStr;
  }
};

const WORKFLOW_ONLY_STATUSES = new Set([
  "draft",
  "pending",
  "approved",
  "signed",
  "rejected",
  "on_approval",
  "active",
  "closed",
]);

const resolveFooterPaymentStatus = (agreement, paymentRows) => {
  const status = agreement?.status;
  if (status && !WORKFLOW_ONLY_STATUSES.has(status)) {
    return status;
  }
  const firstWithStatus = (paymentRows || []).find((row) => row?.status);
  return firstWithStatus?.status ?? resolvePaymentStatus(agreement);
};

const AGREEMENT_STATUSES_WITH_HISTORY = new Set([
  "rejected",
  "approved",
  "signed",
  "draft",
]);

const shouldLoadApprovalHistory = (agreementStatus, activeApproval) =>
  AGREEMENT_STATUSES_WITH_HISTORY.has(agreementStatus) || activeApproval == null;

const AgreementDetail = ({
  agreementId,
  inline = false,
  fallbackCounterpartyName = null,
  sectionNameById = null,
  onApprovalDeleted,
}) => {
  const { userData } = useContext(UserContext);
  const [agreement, setAgreement] = useState(null);
  const [currentApproval, setCurrentApproval] = useState(null);
  const [latestApproval, setLatestApproval] = useState(null);
  const [approvalHistory, setApprovalHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isDeletingApproval, setIsDeletingApproval] = useState(false);
  const [showDeleteApprovalConfirm, setShowDeleteApprovalConfirm] =
    useState(false);
  const [sectionCatalog, setSectionCatalog] = useState(null);

  const loadData = useCallback(async () => {
    if (!agreementId) {
      setAgreement(null);
      setCurrentApproval(null);
      setLatestApproval(null);
      setApprovalHistory([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const agreementData = await getAgreement(agreementId);
      const needsApprovalData = AGREEMENT_STATUSES_WITH_APPROVAL_PROCESS.has(
        agreementData?.status,
      );

      let activeApproval = null;
      let historyList = [];

      if (needsApprovalData) {
        activeApproval = await getCurrentAgreementApproval(agreementId).catch(
          () => null,
        );

        if (shouldLoadApprovalHistory(agreementData?.status, activeApproval)) {
          historyList = await getAgreementApprovals(agreementId).catch(() => []);
          historyList = Array.isArray(historyList) ? historyList : [];
        }
      }

      const latestFromHistory = pickLatestApprovalFromHistory(historyList);
      const resolvedLatest =
        activeApproval?.id != null ? activeApproval : latestFromHistory;

      setAgreement(agreementData);
      setCurrentApproval(activeApproval?.id != null ? activeApproval : null);
      setLatestApproval(resolvedLatest);
      setApprovalHistory(historyList);
    } catch (err) {
      setError(err.message || "Не удалось загрузить соглашение");
      setAgreement(null);
      setCurrentApproval(null);
      setLatestApproval(null);
      setApprovalHistory([]);
    } finally {
      setLoading(false);
    }
  }, [agreementId]);

  useEffect(() => {
    let cancelled = false;
    loadData().then(() => {
      if (cancelled) return;
    });
    return () => {
      cancelled = true;
    };
  }, [loadData]);

  useEffect(() => {
    const projectId = agreement?.project_id;
    if (projectId == null) {
      setSectionCatalog(null);
      return undefined;
    }

    let cancelled = false;
    getProjectEstimate(Number(projectId))
      .then((estimate) => {
        if (!cancelled) setSectionCatalog(buildAgreementSectionCatalog(estimate));
      })
      .catch(() => {
        if (!cancelled) setSectionCatalog({});
      });

    return () => {
      cancelled = true;
    };
  }, [agreement?.project_id]);

  const formatCurrency = (amount) => {
    if (amount == null) return "—";
    return new Intl.NumberFormat("ru-RU").format(amount);
  };

  const getPaymentStatusClass = (status) => {
    if (!status) return "";
    if (
      status === "overdue" ||
      status === "paid_overdue" ||
      status === "overdue_OPGE" ||
      status === "overdue_advance"
    )
      return "agreement-detail-status--overdue";
    if (
      status === "expected" ||
      status === "expected_ODGE" ||
      status === "expected_OPGE"
    )
      return "agreement-detail-status--expected";
    if (status === "paid" || status === "paid_advance")
      return "agreement-detail-status--paid";
    return "";
  };

  const counterpartyName =
    agreement?.counterparty_summary?.display_name ??
    agreement?.counterparty_summary?.full_name ??
    agreement?.counterparty_summary?.full_company_name ??
    agreement?.counterparty_summary?.short_company_name ??
    agreement?.counterparty_name ??
    (Array.isArray(agreement?.performer_names) &&
    agreement.performer_names.length > 0
      ? agreement.performer_names.filter(Boolean).join(", ")
      : null) ??
    fallbackCounterpartyName ??
    (agreement?.counterparty_id != null
      ? `Контрагент #${agreement.counterparty_id}`
      : agreement?.company_id != null
        ? `Контрагент #${agreement.company_id}`
        : "—");

  const workflowStages = useMemo(
    () =>
      normalizeWorkflowStages(agreement?.workflow_stages).map((stage) => ({
        ...stage,
        originalStatus: stage.status,
        status: toTimelineStatus(stage.status),
      })),
    [agreement?.workflow_stages],
  );

  const stages = useMemo(
    () =>
      workflowStages.map((stage, index) => {
        const hasStageName = stage.stage_name && stage.stage_name !== "—";
        const isApprover = Boolean(stage.executor_name);
        const approverStatus = isApprover
          ? toApproverTimelineStatus(stage.originalStatus)
          : null;
        return {
          name: isApprover
            ? stage.executor_name
            : hasStageName
              ? stage.stage_name
              : "—",
          key: `${stage.executor_name || stage.stage_name || "stage"}-${index}`,
          status: approverStatus || stage.status || "not_started",
          statusLabel: approverStatus
            ? APPROVER_TIMELINE_STATUS_LABELS[approverStatus]
            : STAGE_STATUS_LABELS[stage.status] ?? "Не начат",
          decidedAt: isApprover ? stage.decided_at : null,
          isApprover,
        };
      }),
    [workflowStages],
  );

  const currentWorkflowStage = useMemo(
    () => getCurrentWorkflowStageFromList(workflowStages),
    [workflowStages],
  );

  const paymentRows = useMemo(
    () => (agreement ? buildPaymentRows(agreement) : []),
    [agreement],
  );

  const currentStage = agreement?.current_stage ?? {};

  const stageApprovalStatus = toApproverTimelineStatus(
    currentWorkflowStage?.originalStatus ??
      currentWorkflowStage?.status ??
      currentStage.status,
  );

  const blockLabels = useMemo(() => {
    const catalog = { ...(sectionCatalog || {}) };
    Object.entries(sectionNameById || {}).forEach(([id, name]) => {
      const sectionId = toNumericId(id);
      if (sectionId == null || !name) return;
      catalog[sectionId] = {
        blockIds: [],
        blockNameById: {},
        ...(catalog[sectionId] || {}),
        name: catalog[sectionId]?.name || name,
      };
    });
    const resolvedCatalog = Object.keys(catalog).length > 0 ? catalog : null;
    return buildAgreementSectionBlockLabels(agreement, resolvedCatalog);
  }, [agreement, sectionCatalog, sectionNameById]);
  const overallPaymentStatus = resolveFooterPaymentStatus(agreement, paymentRows);

  const displayApproval = latestApproval?.id != null ? latestApproval : currentApproval;

  const pastApprovals = approvalHistory.filter(
    (item) =>
      !displayApproval || Number(item.id) !== Number(displayApproval.id),
  );

  const workflowListStatus = resolveAgreementListStatus(agreement);
  const hadApprovalProcess = ["pending", "rejected", "approved"].includes(
    workflowListStatus,
  );

  const canDeleteCurrentApproval = canShowDeleteApprovalAction(
    agreement,
    userData?.id,
    displayApproval,
  );

  const deleteApprovalTitle = getDeleteApprovalTitle(agreement);
  const initiatorName = resolveApprovalInitiatorName(displayApproval, agreement);

  const handleDeleteApproval = async () => {
    if (
      !canDeleteCurrentApproval ||
      !agreement ||
      !isAgreementApprovalInitiator(displayApproval, agreement, userData?.id)
    ) {
      return;
    }
    setIsDeletingApproval(true);
    setError(null);
    try {
      const approvalId = await resolveAgreementApprovalId(agreement);
      if (approvalId == null) {
        throw new Error("Процесс согласования не найден");
      }
      await deleteAgreementApproval(approvalId);
      setShowDeleteApprovalConfirm(false);
      onApprovalDeleted?.();
      await loadData();
    } catch (err) {
      setError(err.message || "Не удалось отменить согласование");
    } finally {
      setIsDeletingApproval(false);
    }
  };

  if (!agreementId) return null;

  const content = (
    <>
      {loading && <div className="agreement-detail-loading">Загрузка...</div>}

      {error && (
        <div className="agreement-detail-error" role="alert">
          {error}
        </div>
      )}

      {!loading && agreement && (
        <>
          <h2 className="agreement-detail-title">
            Этапы по соглашению с {counterpartyName}
          </h2>

          <section className="agreement-detail-section">
            <div className="agreement-detail-section-header">
              <h3 className="agreement-detail-section-title">СОГЛАСОВАНИЕ</h3>
              {canDeleteCurrentApproval && (
                <button
                  type="button"
                  className="agreement-detail-cancel-approval-btn"
                  onClick={() => setShowDeleteApprovalConfirm(true)}
                  disabled={isDeletingApproval}
                >
                  {deleteApprovalTitle}
                </button>
              )}
            </div>
            {displayApproval ? (
              <div className="agreement-detail-approval-card">
                <div className="agreement-detail-approval-meta">
                  <span
                    className={`agreement-detail-approval-status ${getApprovalStatusClass(displayApproval.status)}`}
                  >
                    {APPROVAL_STATUS_LABELS[displayApproval.status] ??
                      displayApproval.status ??
                      "—"}
                  </span>
                  <span className="agreement-detail-approval-date">
                    Отправлено: {formatDateTime(displayApproval.created_at)}
                  </span>
                  {initiatorName && (
                    <span className="agreement-detail-approval-initiator">
                      Инициатор: {initiatorName}
                    </span>
                  )}
                </div>
                {displayApproval.comment?.trim() && (
                  <div className="agreement-detail-approval-comment">
                    <span className="agreement-detail-approval-comment-label">
                      Комментарий при отправке:
                    </span>
                    <span>{displayApproval.comment}</span>
                  </div>
                )}
              </div>
            ) : stages.some((stage) => stage.isApprover) ? null : (
              <p className="agreement-detail-approval-empty">
                {hadApprovalProcess
                  ? "Детали процесса согласования недоступны"
                  : "Соглашение не отправлялось на согласование"}
              </p>
            )}

            <div className="agreement-detail-timeline">
              {stages.map((stage, idx) => (
                <div
                  key={stage.key ?? idx}
                  className={`agreement-detail-timeline-step agreement-detail-timeline-step--${stage.status ?? "not_started"}`}
                >
                  <div className="agreement-detail-timeline-icon">
                    {stage.status === "approved" || stage.status === "completed"
                      ? "✓"
                      : stage.status === "rejected"
                        ? "✕"
                        : idx + 1}
                  </div>
                  <div className="agreement-detail-timeline-label">
                    {stage.name}
                  </div>
                  <div className="agreement-detail-timeline-status">
                    {stage.statusLabel}
                  </div>
                  {stage.decidedAt ? (
                    <div className="agreement-detail-timeline-time">
                      {formatDateTime(stage.decidedAt)}
                    </div>
                  ) : null}
                  {idx < stages.length - 1 && (
                    <div className="agreement-detail-timeline-connector" />
                  )}
                </div>
              ))}
            </div>
          </section>

          {showDeleteApprovalConfirm && (
            <div
              className="agreement-detail-approval-confirm"
              role="alertdialog"
              aria-labelledby="agreement-detail-delete-approval-title"
            >
              <p id="agreement-detail-delete-approval-title">
                Отменить отправку на согласование? Процесс согласования будет
                удалён.
              </p>
              <div className="agreement-detail-approval-confirm-actions">
                <button
                  type="button"
                  className="agreement-detail-approval-confirm-btn agreement-detail-approval-confirm-btn--cancel"
                  onClick={() => setShowDeleteApprovalConfirm(false)}
                  disabled={isDeletingApproval}
                >
                  Нет
                </button>
                <button
                  type="button"
                  className="agreement-detail-approval-confirm-btn agreement-detail-approval-confirm-btn--confirm"
                  onClick={handleDeleteApproval}
                  disabled={isDeletingApproval}
                >
                  {isDeletingApproval ? "Удаление..." : "Отменить согласование"}
                </button>
              </div>
            </div>
          )}

          {pastApprovals.length > 0 && (
            <section className="agreement-detail-section">
              <h3 className="agreement-detail-section-title">
                ИСТОРИЯ СОГЛАСОВАНИЙ
              </h3>
              <div className="agreement-detail-approval-history">
                {[...pastApprovals]
                  .sort(
                    (a, b) =>
                      new Date(b.created_at).getTime() -
                      new Date(a.created_at).getTime(),
                  )
                  .map((approval) => (
                    <details
                      key={approval.id}
                      className="agreement-detail-approval-history-item"
                    >
                      <summary className="agreement-detail-approval-history-summary">
                        <span
                          className={`agreement-detail-approval-status ${getApprovalStatusClass(approval.status)}`}
                        >
                          {APPROVAL_STATUS_LABELS[approval.status] ??
                            approval.status ??
                            "—"}
                        </span>
                        <span className="agreement-detail-approval-history-date">
                          {formatDateTime(approval.created_at)}
                        </span>
                        {approval.initiator_name && (
                          <span className="agreement-detail-approval-initiator">
                            Инициатор: {approval.initiator_name}
                          </span>
                        )}
                        {approval.comment?.trim() && (
                          <span className="agreement-detail-approval-history-comment">
                            «{approval.comment}»
                          </span>
                        )}
                      </summary>
                      <div className="agreement-detail-approval-history-body">
                        <ApprovalStepsTable steps={approval.steps} />
                      </div>
                    </details>
                  ))}
              </div>
            </section>
          )}

          <section className="agreement-detail-section">
            <h3 className="agreement-detail-section-title">ЭТАП</h3>
            <div className="agreement-detail-stage-grid">
              <div className="agreement-detail-stage-item">
                <label>Дата начала (план)</label>
                <div>{formatDateRu(currentStage.plan_start ?? agreement.date_from) ?? "—"}</div>
              </div>
              <div className="agreement-detail-stage-item">
                <label>Дата окончания (план)</label>
                <div
                  className={
                    currentStage.plan_end_overdue
                      ? "agreement-detail-date-overdue"
                      : ""
                  }
                >
                  {formatDateRu(currentStage.plan_end ?? agreement.date_to) ?? "—"}
                  {currentStage.plan_end_overdue && " (просрочено)"}
                </div>
              </div>
              <div className="agreement-detail-stage-item">
                <label>Дата начала (факт)</label>
                <div>{formatDateRu(currentStage.fact_start) ?? "—"}</div>
              </div>
              <div className="agreement-detail-stage-item">
                <label>Дата окончания (факт)</label>
                <div>{formatDateRu(currentStage.fact_end) ?? "—"}</div>
              </div>
              <div className="agreement-detail-stage-item agreement-detail-stage-item--full">
                <label>Статус</label>
                <span
                  className={`agreement-detail-status-badge agreement-detail-status-badge--${stageApprovalStatus}`}
                >
                  {APPROVER_TIMELINE_STATUS_LABELS[stageApprovalStatus]}
                </span>
              </div>
            </div>
            {currentStage.alert_message && (
              <div className="agreement-detail-alert" role="alert">
                <span className="agreement-detail-alert-icon">ℹ</span>
                {currentStage.alert_message}
              </div>
            )}
          </section>

          <section className="agreement-detail-section">
            <h3 className="agreement-detail-section-title">СТАТУС ОПЛАТЫ</h3>
            <div className="agreement-detail-table-wrap">
              <table className="agreement-detail-table">
                <thead>
                  <tr>
                    <th>Платёж</th>
                    <th>%</th>
                    <th>Сумма (план)</th>
                    <th>Сумма (факт)</th>
                    <th>Дата (план)</th>
                    <th>Дата (факт)</th>
                    <th>Статус</th>
                  </tr>
                </thead>
                <tbody>
                  {paymentRows.length === 0 ? (
                    <tr>
                      <td colSpan={7}>График оплат не задан</td>
                    </tr>
                  ) : (
                    paymentRows.map((row) => (
                      <tr key={row.key}>
                        <td>{row.label}</td>
                        <td>{row.percent != null ? `${row.percent}%` : "—"}</td>
                        <td>{formatCurrency(row.plannedAmount)} ₸</td>
                        <td>{formatCurrency(row.payment?.fact_amount)}</td>
                        <td>{formatDateRu(row.plannedDate) ?? "—"}</td>
                        <td>{formatDateRu(row.paidAt) ?? "—"}</td>
                        <td>
                          {row.status ? (
                            <span
                              className={`agreement-detail-status ${getPaymentStatusClass(row.status)}`}
                            >
                              {PAYMENT_STATUS_LABELS[row.status] ?? row.status}
                            </span>
                          ) : (
                            "—"
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </section>

          <div className="agreement-detail-footer">
            <div className="agreement-detail-footer-left">
              <span className="agreement-detail-footer-subcontractor">
                &gt; {counterpartyName}
              </span>
              {blockLabels.length > 0 && (
                <span className="agreement-detail-footer-blocks">
                  {blockLabels.map((b, i) => (
                    <span key={i} className="agreement-detail-footer-block-tag">
                      {b}
                    </span>
                  ))}
                </span>
              )}
              <span className="agreement-detail-footer-total">
                ₸ {formatCurrency(agreement.price)}
              </span>
            </div>
            <div className="agreement-detail-footer-right">
              <span className="agreement-detail-footer-stage-badge">
                {currentWorkflowStage?.stage_name &&
                currentWorkflowStage.stage_name !== "—"
                  ? `${currentWorkflowStage.stage_name} · `
                  : ""}
                {APPROVER_TIMELINE_STATUS_LABELS[stageApprovalStatus]}
              </span>
              {overallPaymentStatus && (
                <span
                  className={`agreement-detail-status agreement-detail-status--footer ${getPaymentStatusClass(overallPaymentStatus)}`}
                >
                  {PAYMENT_STATUS_LABELS[overallPaymentStatus] ??
                    overallPaymentStatus}
                </span>
              )}
              <button
                type="button"
                className="agreement-detail-action view-btn"
                title="Просмотр"
              >
                👁
              </button>
              <button
                type="button"
                className="agreement-detail-action edit-btn"
                title="Редактировать"
              >
                ✎
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );

  return (
    <div className={inline ? "agreement-detail-inline" : "agreement-detail-modal"}>
      {content}
    </div>
  );
};

export default AgreementDetail;
